/**
 * About Page Component
 */

import { memo } from 'react';

export const AboutPage = memo(function AboutPage() {
  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-white mb-8">About ImageLab</h1>
      
      <div className="prose prose-invert max-w-none space-y-6 text-slate-300">
        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Our Mission</h2>
          <p>
            ImageLab is a free online tool designed for web developers, designers, and anyone 
            who works with vector graphics. Our mission is to simplify the process of converting and 
            manipulating images for the web, all while keeping your files private and secure.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">What We Offer</h2>
          <p>
            Whether you're building a website, creating a web application, or working on a design 
            system, ImageLab helps you work more efficiently with a comprehensive suite of 
            image tools:
          </p>
          
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-medium text-indigo-400 mb-2">SVG to Code</h3>
              <p className="text-sm text-slate-400">
                Convert SVG files to clean, optimized code with syntax highlighting. 
                Perfect for embedding directly in your HTML.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-medium text-indigo-400 mb-2">PNG to SVG</h3>
              <p className="text-sm text-slate-400">
                Transform raster images into scalable vector graphics using advanced 
                tracing algorithms.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-medium text-indigo-400 mb-2">Favicon Generator</h3>
              <p className="text-sm text-slate-400">
                Create complete favicon packages with all sizes, ICO files, and 
                manifest configurations.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-medium text-indigo-400 mb-2">Image Compressor</h3>
              <p className="text-sm text-slate-400">
                Reduce image file sizes by up to 80% while maintaining visual quality 
                for faster loading websites.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-medium text-indigo-400 mb-2">Image Converter</h3>
              <p className="text-sm text-slate-400">
                Convert between PNG, JPG, WebP, ICO, and BMP formats with full 
                control over quality settings.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-medium text-indigo-400 mb-2">SVG Editor</h3>
              <p className="text-sm text-slate-400">
                Edit SVG properties like colors, strokes, opacity, and transforms 
                directly in your browser.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Privacy First</h2>
          <p>
            Built with modern web technologies including React and TypeScript, ImageLab 
            runs entirely in your browser. This means <strong className="text-indigo-400">your files 
            are never uploaded to a server</strong> – everything stays on your device, ensuring 
            your designs remain private and secure.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Perfect For</h2>
          <ul className="list-disc list-inside space-y-2 text-slate-400">
            <li>Frontend developers integrating icons and illustrations</li>
            <li>UI/UX designers exporting assets for development</li>
            <li>Graphic designers preparing web-ready graphics</li>
            <li>Marketing teams creating email templates with graphics</li>
            <li>Anyone who needs to work with images for the web</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">The Technology Behind ImageLab</h2>
          <p className="mb-4">
            ImageLab leverages cutting-edge web technologies to deliver a fast, reliable, and secure experience. 
            Built as a single-page application (SPA), it processes everything client-side for maximum privacy and 
            performance.
          </p>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-medium text-indigo-400 mb-2">React & TypeScript</h3>
              <p className="text-sm text-slate-400">
                Built with React 18 for a reactive, component-based architecture. TypeScript ensures code quality 
                and catches errors before they reach production, providing a more stable user experience.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-medium text-indigo-400 mb-2">HTML5 Canvas & FileReader</h3>
              <p className="text-sm text-slate-400">
                We harness the power of HTML5 Canvas API for image processing and manipulation, enabling compression, 
                conversion, and tracing entirely in your browser without server dependencies.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-medium text-indigo-400 mb-2">Vite Build Tool</h3>
              <p className="text-sm text-slate-400">
                Vite provides lightning-fast development builds and optimized production bundles with code splitting, 
                tree shaking, and automatic chunk loading for optimal performance.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-medium text-indigo-400 mb-2">Tailwind CSS</h3>
              <p className="text-sm text-slate-400">
                Responsive, mobile-first design powered by Tailwind CSS ensures ImageLab looks great and works 
                smoothly on devices of all sizes, from phones to 4K displays.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Why We Built ImageLab</h2>
          <p className="mb-3">
            As web developers and designers ourselves, we constantly needed to convert SVG files, compress images, 
            and generate favicons. Existing tools either required uploads (privacy concerns), charged subscription 
            fees, or had clunky interfaces with excessive ads.
          </p>
          <p className="mb-3">
            We wanted something better: a fast, privacy-focused, completely free toolkit that works entirely in the 
            browser. ImageLab is our solution—built by developers, for developers and designers.
          </p>
          <p>
            We believe web tools should respect user privacy, be accessible without paywalls, and provide genuinely 
            useful functionality without unnecessary complexity. That's ImageLab's foundation.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Performance & Reliability</h2>
          <p className="mb-3">
            ImageLab is designed for speed and reliability. All processing happens locally using optimized algorithms 
            that leverage your device's hardware acceleration where available.
          </p>
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <ul className="space-y-2 text-slate-400 text-sm">
              <li className="flex items-start">
                <span className="text-indigo-400 mr-3 mt-0.5">•</span>
                <div>
                  <strong className="text-white">Instant Processing:</strong> No server round-trips mean conversions 
                  happen in milliseconds, not seconds.
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-400 mr-3 mt-0.5">•</span>
                <div>
                  <strong className="text-white">No File Size Restrictions:</strong> Work with files of any size—your 
                  device's memory is the only limit.
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-400 mr-3 mt-0.5">•</span>
                <div>
                  <strong className="text-white">Always Available:</strong> No downtime from server maintenance. If 
                  you can load the page, the tools work.
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-400 mr-3 mt-0.5">•</span>
                <div>
                  <strong className="text-white">Unlimited Usage:</strong> No rate limits, no daily quotas, no premium 
                  tiers. Use it as much as you need.
                </div>
              </li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Commitment to Accessibility</h2>
          <p className="mb-3">
            ImageLab is built with accessibility in mind. We follow WCAG guidelines to ensure our tools are usable 
            by everyone, regardless of ability or device.
          </p>
          <ul className="list-disc list-inside space-y-2 text-slate-400">
            <li>Keyboard navigation support for all tools and controls</li>
            <li>Proper semantic HTML and ARIA labels for screen readers</li>
            <li>High contrast color schemes for better visibility</li>
            <li>Responsive design that works on assistive technology</li>
            <li>Clear error messages and helpful tooltips</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Future Roadmap</h2>
          <p className="mb-4">
            We're continuously improving ImageLab based on user feedback. Here's what we're working on:
          </p>
          <div className="space-y-2">
            <div className="flex items-start">
              <span className="text-green-400 mr-3 mt-0.5">✓</span>
              <div className="text-slate-300">
                <strong className="text-white">Batch Processing:</strong> Convert multiple files at once for increased 
                productivity.
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-green-400 mr-3 mt-0.5">✓</span>
              <div className="text-slate-300">
                <strong className="text-white">Progressive Web App (PWA):</strong> Install ImageLab as a desktop app 
                with offline support.
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-green-400 mr-3 mt-0.5">✓</span>
              <div className="text-slate-300">
                <strong className="text-white">Advanced SVG Editing:</strong> More powerful editing capabilities 
                including path manipulation.
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-green-400 mr-3 mt-0.5">✓</span>
              <div className="text-slate-300">
                <strong className="text-white">AVIF Support:</strong> Support for the next-generation AVIF image format.
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-green-400 mr-3 mt-0.5">✓</span>
              <div className="text-slate-300">
                <strong className="text-white">Image Editing Tools:</strong> Crop, rotate, resize, and filter images 
                directly in the browser.
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Perfect For</h2>
          <ul className="list-disc list-inside space-y-2 text-slate-400">
            <li>Frontend developers integrating icons and illustrations into web projects</li>
            <li>UI/UX designers exporting assets from design tools for development handoff</li>
            <li>Graphic designers preparing web-ready graphics for client websites</li>
            <li>Marketing teams creating email templates and social media graphics</li>
            <li>Content creators optimizing images for blogs and publications</li>
            <li>Web agencies managing assets across multiple client projects</li>
            <li>Anyone who works with digital images and wants better, faster tools</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Open Source</h2>
          <p>
            ImageLab is built with love using open-source technologies. We believe in 
            giving back to the community that makes tools like this possible.
          </p>
          <div className="mt-4">
            <a 
              href="https://github.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
              View on GitHub
            </a>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Support</h2>
          <p>
            ImageLab is free to use, but if you find it helpful, consider supporting 
            the project:
          </p>
          <div className="mt-4">
            <a 
              href="https://buymeacoffee.com/akalankaih4" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-medium rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.216 6.415l-.132-.666c-.119-.598-.388-1.163-1.001-1.379-.197-.069-.42-.098-.57-.241-.152-.143-.196-.366-.231-.572-.065-.378-.125-.756-.192-1.133-.057-.325-.102-.69-.25-.987-.195-.4-.597-.634-.996-.788a5.723 5.723 0 00-.626-.194c-1-.263-2.05-.36-3.077-.416a25.834 25.834 0 00-3.7.062c-.915.083-1.88.184-2.75.5-.318.116-.646.256-.888.501-.297.302-.393.77-.177 1.146.154.267.415.456.692.58.36.162.737.284 1.123.366 1.075.238 2.189.331 3.287.37 1.218.05 2.437.01 3.65-.118.299-.033.598-.073.896-.119.352-.054.578-.513.474-.834-.124-.383-.457-.531-.834-.473-.466.074-.96.108-1.382.146-1.177.08-2.358.082-3.536.006a22.228 22.228 0 01-1.157-.107c-.086-.01-.18-.025-.258-.036-.243-.036-.484-.08-.724-.13-.111-.027-.111-.185 0-.212h.005c.277-.06.557-.108.838-.147h.002c.131-.009.263-.032.394-.048a25.076 25.076 0 013.426-.12c.674.019 1.347.067 2.017.144l.228.031c.267.04.533.088.798.145.392.085.895.113 1.07.542.055.137.08.288.111.431l.319 1.484a.237.237 0 01-.199.284h-.003c-.037.006-.075.01-.112.015a36.704 36.704 0 01-4.743.295 37.059 37.059 0 01-4.699-.304c-.14-.017-.293-.042-.417-.06-.326-.048-.649-.108-.973-.161-.393-.065-.768-.032-1.123.161-.29.16-.527.404-.675.701-.154.316-.199.66-.267 1-.069.34-.176.707-.135 1.056.087.753.613 1.365 1.37 1.502a39.69 39.69 0 0011.343.376.483.483 0 01.535.53l-.071.697-1.018 9.907c-.041.41-.047.832-.125 1.237-.122.637-.553 1.028-1.182 1.171-.577.131-1.165.2-1.756.205-.656.004-1.31-.025-1.966-.022-.699.004-1.556-.06-2.095-.58-.475-.458-.54-1.174-.605-1.793l-.731-7.013-.322-3.094c-.037-.351-.286-.695-.678-.678-.336.015-.718.3-.678.679l.228 2.185.949 9.112c.147 1.344 1.174 2.068 2.446 2.272.742.12 1.503.144 2.257.156.966.016 1.942.053 2.892-.122 1.408-.258 2.465-1.198 2.616-2.657.34-3.332.683-6.663 1.024-9.995l.215-2.087a.484.484 0 01.39-.426c.402-.078.787-.212 1.074-.518.455-.488.546-1.124.385-1.766zm-1.478.772c-.145.137-.363.201-.578.233-2.416.359-4.866.54-7.308.46-1.748-.06-3.477-.254-5.207-.498-.17-.024-.353-.055-.47-.18-.22-.236-.111-.71-.054-.995.052-.26.152-.609.463-.646.484-.057 1.046.148 1.526.22.577.088 1.156.159 1.737.212 2.48.226 5.002.19 7.472-.14.45-.06.899-.13 1.345-.21.399-.072.84-.206 1.08.206.166.281.188.657.162.974a.544.544 0 01-.169.364z"/>
              </svg>
              Buy me a coffee
            </a>
          </div>
        </section>
      </div>
    </div>
  );
});
